// Campañas de pauta y anuncios con código (Marketing M1-2). Funciones puras, con pruebas.
import { addDias } from './fechas';
import { cuenta, totalesPedido, type Pedido } from './pedidos';
import { sinTildes } from './texto';

export type Objetivo = 'nuevos' | 'reactivar' | 'ambos';
export type EstadoCampana = 'borrador' | 'activa' | 'pausada' | 'terminada';
export interface Anuncio { id: number; campana_id: number; codigo: string; nombre: string; pieza: string | null; mensaje: string | null; activo: boolean }
export interface Campana {
  id: number; codigo: string; nombre: string; objetivo: Objetivo; plataforma: string; inicio: string | null; fin: string | null;
  presupuesto: number; estado: EstadoCampana; segmento: string | null; notas: string | null; anuncios: Anuncio[];
  tope_cpp: number | null; // lo máximo que se acepta pagar en pauta por pedido (COP), para el semáforo del tablero
}
export const OBJETIVOS: { id: Objetivo; label: string }[] = [{ id: 'nuevos', label: 'Clientes nuevos' }, { id: 'reactivar', label: 'Reactivar clientes' }, { id: 'ambos', label: 'Ambos' }];
export const ESTADOS_CAMPANA: { id: EstadoCampana; label: string; tono: string }[] = [
  { id: 'borrador', label: 'En preparación', tono: '' }, { id: 'activa', label: 'Activa', tono: 'ok' }, { id: 'pausada', label: 'Pausada', tono: 'warn' }, { id: 'terminada', label: 'Terminada', tono: 'info' }];
export const PLATAFORMAS = ['Instagram', 'Facebook', 'Instagram y Facebook', 'TikTok', 'Google', 'Otra'];
export const MENSAJE_BASE = 'Hola Talentino 👋 Quiero información de las prendas personalizadas';

// Códigos en mayúsculas, sin tildes ni espacios: "nav 26" → "NAV26".
export const limpiarCodigo = (s: string) => sinTildes(s).toUpperCase().replace(/[^A-Z0-9]/g, '');
export const codigoCampanaError = (s: string) => (/^[A-Z0-9]{2,8}$/.test(s) ? '' : 'De 2 a 8 letras o números, sin espacios (ej.: NAV).');
// El siguiente anuncio de la campaña: NAV1, NAV2… (sigue al número más alto, aunque se haya borrado uno).
export function siguienteCodigo(campana: Pick<Campana, 'codigo'>, anuncios: Pick<Anuncio, 'codigo'>[]): string {
  const n = anuncios.map(a => (a.codigo.startsWith(campana.codigo) ? +a.codigo.slice(campana.codigo.length) : 0)).filter(Number.isFinite);
  return campana.codigo + (Math.max(0, ...n) + 1);
}
// Mensaje prellenado del anuncio: el texto y, al final, el código entre paréntesis para saber de qué anuncio llegó.
export const mensajeAnuncio = (a: Pick<Anuncio, 'mensaje' | 'codigo'>) => `${(a.mensaje || MENSAJE_BASE).trim()} (${a.codigo})`;
// Nombre que el anuncio debe llevar en el Administrador de anuncios: con el código al inicio, para cruzar el reporte de Meta.
export const nombreEnMeta = (a: Pick<Anuncio, 'codigo' | 'nombre'>) => `${a.codigo} ${a.nombre}`;
export const enlaceWhatsApp = (numero: string, texto: string) => `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
// Busca un código de anuncio en lo que escribió el cliente: "… prendas personalizadas (NAV2)".
export function anuncioEnTexto(texto: string, campanas: Campana[]): Anuncio | null {
  const codigos: string[] = texto.toUpperCase().match(/[A-Z0-9]{3,10}/g) || [];
  for (const c of campanas) for (const a of c.anuncios) if (codigos.includes(a.codigo)) return a;
  return null;
}

// Días de la campaña (incluidos el primero y el último) y cuánto se puede gastar por día.
export function diasCampana(c: Pick<Campana, 'inicio' | 'fin'>): number {
  if (!c.inicio || !c.fin) return 0;
  let n = 0;
  for (let d = c.inicio; d <= c.fin && n < 1000; d = addDias(d, 1)) n++;
  return n;
}
export const presupuestoDiario = (c: Pick<Campana, 'inicio' | 'fin' | 'presupuesto'>) => { const d = diasCampana(c); return d ? Math.round(c.presupuesto / d) : 0; };

// Lo que trajo cada campaña y cada anuncio: pedidos que son venta, ventas en pesos, cotizaciones abiertas.
export interface Atribuido { pedidos: number; ventas: number; cotizaciones: number }
export function atribucion(pedidos: Pedido[]) {
  const porCampana = new Map<number, Atribuido>(), porAnuncio = new Map<number, Atribuido>();
  const sumar = (m: Map<number, Atribuido>, id: number, p: Pedido) => {
    const x = m.get(id) || { pedidos: 0, ventas: 0, cotizaciones: 0 };
    if (p.estado === 'cotizacion') x.cotizaciones++;
    else if (cuenta(p)) { const t = totalesPedido(p); x.pedidos++; x.ventas += t.total * t.tasa; }
    m.set(id, x);
  };
  for (const p of pedidos) {
    if (p.pauta_id) sumar(porCampana, p.pauta_id, p);
    if (p.anuncio_id) sumar(porAnuncio, p.anuncio_id, p);
  }
  return { porCampana, porAnuncio };
}
